import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getHomeFaqs } from "../../../api/faqApi";
import { ChevronDown, ArrowRight } from "lucide-react";
import FaqBlockRenderer from "../../../components/FaqBlockRenderer";
import { FaqShimmer } from "../../../components/ShimmerEffect";

const FaqSection = () => {
  const [faqs, setFaqs] = useState([]);
  const [openFaqId, setOpenFaqId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await getHomeFaqs();
        if (response.success && response.data.length > 0) {
          // Flatten the grouped FAQs and take the first 5
          const allFaqs = response.data.flatMap(topic => topic.faqs);
          setFaqs(allFaqs.slice(0, 5));
        }
      } catch (error) {
        console.error("Error fetching FAQs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFaqs();
  }, []);

  if (loading) {
    return <FaqShimmer />;
  }
  if (faqs.length === 0) return null;

  return (
    <section id="faq" className="pt-6 pb-16 bg-gray-50 dark:bg-gray-900 transition-colors duration-300 scroll-mt-16 sm:scroll-mt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">
            Frequently Asked Questions
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
            Quick answers to some of the most common questions we get.
          </p>
        </div>

        <div className="flex flex-col space-y-4">
          {faqs.map((faq) => {
            const isOpen = openFaqId === faq._id;
            
            return (
              <div
                key={faq._id}
                className={`border rounded-xl transition-all duration-300 overflow-hidden ${
                  isOpen 
                    ? 'border-blue-200 dark:border-blue-800 shadow-md bg-white dark:bg-gray-800' 
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <button
                  onClick={() => setOpenFaqId(isOpen ? null : faq._id)}
                  className="flex justify-between items-center w-full px-6 py-5 text-left focus:outline-none"
                >
                  <span className={`text-lg font-medium pr-8 ${isOpen ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'}`}>
                    {faq.question}
                  </span>
                  <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isOpen ? 'bg-orange-500 text-white rotate-180' : 'bg-gray-100 dark:bg-[var(--surface-elevated)] text-gray-500 dark:text-[var(--text-muted)]'
                  }`}>
                    <ChevronDown className="w-4 h-4" strokeWidth={2.5} />
                  </div>
                </button>
                
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-5 pt-1 border-t border-gray-100 dark:border-[var(--surface-border)]">
                    <div className="pt-3 text-gray-600 dark:text-[var(--text-secondary)]">
                    {typeof faq.answer === 'string' ? (
                      <div className="leading-relaxed whitespace-pre-wrap text-sm">{faq.answer}</div>
                    ) : (
                      <FaqBlockRenderer blocks={faq.answer?.blocks} />
                    )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/faq"
            className="inline-flex items-center justify-center px-8 py-3 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors duration-300"
          >
            View All FAQs
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
